<script lang="ts" setup>
import type { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { computed, ref, watchEffect, type Ref } from 'vue';
import KpFormModal from '@kp/geofy-vuejs-lib/tg-system/components/KpFormModal.vue';
import FileUpload from './FileUpload.vue';
import { createModel } from '@kp/js-model';
import { PlanningPermissionApplicationComment } from '@/models/PlanningPermissionApplicationComment';
import { errToStr } from '@/helpers/tools';
import { useAuthUser } from '@/helpers/globals';

const show = defineModel({ default: false });

const user = useAuthUser();

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const emit = defineEmits<{
    (e: 'created', value: PlanningPermissionApplicationComment): void;
    (e: 'canceled'): void;
}>();

const loading: Ref<boolean> = ref(false);
const msg: Ref<string> = ref('');
const messageObject: Ref<PlanningPermissionApplicationComment | null> = ref(null);
const fileUpload: Ref<typeof FileUpload | null> = ref(null);
const error: Ref<string | null> = ref(null);

const formValid = computed(() => msg.value.trim().length > 0);

watchEffect(() => {
    // reset dialog on show - to clear previously used data:
    if (show.value) {
        msg.value = '';
        messageObject.value = null;
        if (fileUpload.value) {
            fileUpload.value.reset();
            fileUpload.value.clear();
        }
    }
});

async function onSubmit() {
    loading.value = true;
    error.value = null;
    try {
        // Step 1: store the message, as draft:
        messageObject.value = createModel(PlanningPermissionApplicationComment, {
            planning_permission_application_id: props.geschaeft.id,
            draft: true,
            comment: msg.value
        });
        await messageObject.value.save();
        if (!messageObject.value?.id) {
            console.error('Something went wrong during message saving');
        }

        // Step 2: Upload documents:
        await fileUpload.value?.uploadFiles();

        // Step 3: Mark the message as non-draft, when everything went well:
        messageObject.value.draft = false;
        await messageObject.value.save();

        // ...and emit & close:
        emit('created', messageObject.value);
        show.value = false;
    } catch (e) {
        console.error(e);
        error.value = errToStr(e);
    } finally {
        loading.value = false;
    }
}

function onCancel() {
    show.value = false;
}
</script>

<template>
    <KpFormModal v-model="show">
        <form @submit.prevent="onSubmit">
            <h2 class="ktg-form-modal-title">
                {{ user?.isRoleGemeinde ? $t('mitteilung_an_gesuchsteller') : $t('mitteilung_an_gemeinde') }}
            </h2>
            <ktg-field>
                <ktg-label for="msg">{{ $t('mitteilung') }} *</ktg-label>
                <ktg-text-area
                    input-id="msg"
                    class="w-full"
                    :value="msg"
                    :placeholder="$t('mitteilung_erfassen')"
                    @input="msg = $event.target.value"
                    :maxlength="1000"
                    rows="10"
                />
            </ktg-field>

            <FileUpload
                ref="fileUpload"
                :auto-start-upload="false"
                entity="planning_permission_application_comment"
                :parent-id="messageObject?.id || 0"
                :readonly="false"
                :title="$t('documents')"
                class="mt-0_5rem"
                @upload-in-progress="loading = $event"
            />

            <ktg-alert-banner v-if="error" appearance="danger" class="my-1_5rem">
                {{ error }}
            </ktg-alert-banner>
        </form>
        <ktg-form-modal-buttons>
            <ktg-button appearance="secondary" full-width :disabled="loading" @click="onCancel">
                {{ $t('cancel') }}
            </ktg-button>
            <ktg-button
                appearance="primary"
                full-width
                :loading="loading"
                :disabled="loading || !formValid"
                type="submit"
            >
                {{ $t('mitteilung_absenden') }}
            </ktg-button>
        </ktg-form-modal-buttons>
    </KpFormModal>
</template>

<style lang="scss" scoped></style>
